import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiFileText,
  FiDownload,
  FiDollarSign,
  FiEdit2,
} from 'react-icons/fi';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import invoiceService from '../services/invoiceService';
import Button from '../components/Button';
import './DetailView.css';

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');

  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await invoiceService.getById(id);
      const data = response.data || response;
      setInvoice(data);
      setAmountPaid(data.amountPaid ?? 0);
    } catch (error) {
      toast.error('Failed to fetch invoice details');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!invoice) return;
    setDownloadLoading(true);
    try {
      const blob = await invoiceService.download(id);
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download invoice');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    try {
      const payload = { amountPaid: Number(amountPaid) || 0 };
      const response = await invoiceService.updatePayment(id, payload);
      const data = response.data || response;
      setInvoice(data);
      toast.success('Invoice payment updated');
    } catch (error) {
      toast.error('Failed to update payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const paymentBadgeClass =
    invoice.paymentStatus === 'paid'
      ? 'badge-success'
      : invoice.paymentStatus === 'partial'
      ? 'badge-warning'
      : 'badge-error';

  return (
    <div className="detail-view-container">
      <div className="detail-header">
        <button onClick={() => navigate('/invoices')} className="back-button">
          <FiArrowLeft /> Back to Invoices
        </button>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Button
            variant="secondary"
            icon={<FiEdit2 />}
            onClick={() => navigate(`/invoices/${id}/edit`)}
          >
            Edit Invoice
          </Button>
          <Button
            variant="secondary"
            icon={<FiDownload />}
            loading={downloadLoading}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </div>
      </div>

      <div className="detail-card">
        <div
          className="detail-hero"
          style={{
            background:
              'linear-gradient(135deg, #6366f1 0%, #4f46e5dd 100%)',
          }}
        >
          <div className="hero-avatar">
            <FiFileText />
          </div>
          <div className="hero-info">
            <h1 className="hero-title">
              {invoice.customer?.name || 'Invoice'}
            </h1>
            <p className="hero-subtitle">
              {invoice.invoiceNumber} • Ticket #
              {invoice.siteVisit?.ticketNumber || 'N/A'}
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <span className={`badge ${paymentBadgeClass}`}>
                {invoice.paymentStatus?.toUpperCase()}
              </span>
              <span className="badge badge-gray">
                {invoice.invoiceDate
                  ? format(new Date(invoice.invoiceDate), 'MMM dd, yyyy')
                  : ''}
              </span>
            </div>
          </div>
        </div>

        <div className="detail-sections">
          <div className="detail-section">
            <h3 className="section-title">Customer Information</h3>
            <div className="customer-info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value">
                  {invoice.customer?.name || '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone</span>
                <span className="info-value">
                  {invoice.customer?.phone || '-'}
                </span>
              </div>
              {invoice.customer?.email && (
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{invoice.customer.email}</span>
                </div>
              )}
              {invoice.customer?.address?.city && (
                <div className="info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">
                    {invoice.customer.address.city},{' '}
                    {invoice.customer.address.state}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3 className="section-title">Line Items</h3>
            <div className="items-tools-section">
              {invoice.items?.length > 0 && (
                <div className="items-list">
                  <h4>Items ({invoice.items.length})</h4>
                  {invoice.items.map((item, idx) => (
                    <div key={idx} className="item-row">
                      <span className="item-name">{item.description}</span>
                      <div
                        style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span className="item-qty">
                          Qty: {item.quantity} × ₹
                          {item.unitPrice?.toLocaleString() || 0}
                        </span>
                        <span className="item-qty">
                          Amount: ₹{item.amount?.toLocaleString() || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {invoice.services?.length > 0 && (
                <div className="tools-list">
                  <h4>Services ({invoice.services.length})</h4>
                  {invoice.services.map((service, idx) => (
                    <div key={idx} className="item-row">
                      <span className="item-name">{service.description}</span>
                      <span className="item-qty">
                        ₹{service.amount?.toLocaleString() || 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3 className="section-title">Totals</h3>
            <div className="cost-breakdown">
              <div className="cost-item">
                <span className="cost-label">Subtotal</span>
                <span className="cost-amount">
                  ₹{invoice.subtotal?.toLocaleString() || 0}
                </span>
              </div>
              <div className="cost-item">
                <span className="cost-label">Tax</span>
                <span className="cost-amount">
                  ₹{invoice.tax?.total?.toLocaleString() || 0}
                </span>
              </div>
              {invoice.discount > 0 && (
                <div className="cost-item">
                  <span className="cost-label">Discount</span>
                  <span className="cost-amount">
                    -₹{invoice.discount?.toLocaleString() || 0}
                  </span>
                </div>
              )}
              <div className="cost-item">
                <span className="cost-label">Total Amount</span>
                <span className="cost-amount total">
                  ₹{invoice.totalAmount?.toLocaleString() || 0}
                </span>
              </div>
              <div className="cost-item">
                <span className="cost-label">Amount Paid</span>
                <span className="cost-amount">
                  ₹{invoice.amountPaid?.toLocaleString() || 0}
                </span>
              </div>
              <div className="cost-item">
                <span className="cost-label">Balance Due</span>
                <span className="cost-amount">
                  ₹{invoice.balanceDue?.toLocaleString() || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3 className="section-title">Update Payment</h3>
            <form
              onSubmit={handleUpdatePayment}
              style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
            >
              <div className="input-group" style={{ maxWidth: 240 }}>
                <label className="input-label">Total Paid Amount (₹)</label>
                <input
                  type="number"
                  className="input-field"
                  min="0"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                icon={<FiDollarSign />}
                loading={paymentLoading}
              >
                Save Payment
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;

